import EventsEmitter from "./EventsEmitter";
import CrossSectionBounds from "./CrossSectionBounds";
import {
    handleLayerEvent,
    fromRESTBool,
    getZoomToLayersIds,
    loadTempEntities,
    checkCompositeMesh,
    getLayersAttributes,
} from "actions/ViewerTools";
import {
    getDrillHoleSettings,
    getEntityBounds,
    getLayerSettings,
    getAttributeIndex,
    getAttributesObjects,
    getEntityId,
} from "actions/GlobalActions";
import { getCachedData } from "api/Cache";
import ApiUtils from "api/Utils";
import * as Utils from "services/UtilsService";
import { fetchComments, fetchSnapshots, fetchCustomColors } from "api/Entities";
import { StatsActions, RasterActions, LayersActions, GradientActions, ViewerActions, ViewsActions } from "actions";
import { ColorLegendDefault } from "constants/viewer";

var isFirstTime = false;
console.time("*********completeLoadingTime");

export default class ViewerEvents extends EventsEmitter {
    name = "ViewerEvents";
    static viewer: any;
    static view: any;
    static viewBounds: any;
    static entities: any;
    static currentProject: any;
    static projectId: string;
    static viewMetaData: any;
    static compositeMeta: any;
    static layers: any = {};
    static inputs: any = {};
    static newScene = false;
    static cameraPosInitialized = false;
    static thumbnailPosCaptured = false;
    static metaDataChanged = false;
    static selectedViewCameraOriginCapture: any = {};
    static selectedViewFocusOriginCapture: any = {};
    static pendingCompositeMeshes: any = [];
    static tempEntities: any = [];
    static pendingDeletion: any = [];
    static selectedViewThumbnailCapture = "";

    static initialize(view: any, entities: any) {
        console.log("ViewerEvents initialize...");

        ViewerEvents.view = view;
        ViewerEvents.entities = entities;
        ViewerEvents.projectId = view.project;
        ViewerEvents.newScene = false;
        ViewerEvents.currentProject = Utils.deepCopyObject(entities[ViewerEvents.projectId]);
        ViewerEvents.cameraPosInitialized = false;
        ViewerEvents.thumbnailPosCaptured = false;
        ViewerEvents.selectedViewThumbnailCapture = view.thumb;
        ViewerEvents.selectedViewCameraOriginCapture = {};
        ViewerEvents.selectedViewFocusOriginCapture = {};
        CrossSectionBounds.resetCrossSectionBounds();
        if (view.ID) {
            fetchComments(view.ID).then((result: any) => {
                console.log("comments:", result);
            });
            fetchSnapshots(view.project, view.ID).then((result: any) => {
                console.log("snapshots:", result);
            });
        }
        EventsEmitter.emit("initview", {});
        ViewerEvents.on("destroyView", ViewerEvents.globalDestroy);
    }

    static setViewer(viewer: any) {
        ViewerEvents.viewer = viewer;
        ViewerActions.viewer = viewer;
        /*
        if (ViewerEvents.viewer._loader) {
            ViewerEvents.viewer._loader.loadContainerAttributes = ViewerEvents.loadContainerAttributes;
        }*/
    }

    static globalDestroy() {
        ViewerActions.destroy();
        LayersActions.destroy();
        ViewerEvents.destroy();
        ViewerEvents.removeEventListener("destroyView", ViewerEvents.globalDestroy);
    }

    static destroy() {
        ViewerEvents.viewer = undefined;
        ViewerEvents.view = undefined;
        ViewerEvents.layers = [];
        ViewerEvents.newScene = false;
        ViewerEvents.metaDataChanged = false;
        ViewerEvents.viewMetaData = undefined;
        ViewerEvents.compositeMeta = undefined;
        ViewerEvents.inputs = {};
        ViewerEvents.currentProject = undefined;
        ViewerEvents.projectId = "";

        const cachedData = getCachedData();
        if (Object.keys(cachedData["pending"]).length > 0) {
            Object.values(cachedData["pending"]).forEach((view: any) => {
                ViewsActions.deleteView(view, (viewId: any) => {
                    console.log("delete temp view ", viewId);
                });
            });
        }
        console.log("cleanup " + this.name + " done...");
    }

    static findElement(id: string) {
        if (typeof ViewerEvents.layers[id] !== "undefined") {
            return ViewerEvents.layers[id];
        } else {
            return ViewerEvents.currentProject[id];
        }
    }

    static getViewer() {
        return ViewerEvents.viewer;
    }

    static getPrograms(viewMetaData: any) {
        if (!ViewerEvents.viewer) {
            return;
        }
        var programs = [];
        for (var comp in viewMetaData) {
            var item = ViewerEvents.findElement(comp);
            if (item && item.view_type) {
                programs[item.view_type] = item.view_type;
            }
        }
        return programs;
    }

    static getInputs(programs: any, callback: any) {
        if (!ViewerEvents.viewer || ViewerEvents.viewer === null) {
            return;
        }
        let viewInputs: any = {};
        console.log("programs:", programs);
        let pending = Object.entries(programs).length;
        if (pending === 0) {
            if (callback) {
                callback({});
            }
            return;
        }
        Utils.cleanArray(Object.keys(programs)).forEach((programId: string) => {
            if (ViewerEvents.inputs[programId]) {
                pending--;
                viewInputs = { ...ViewerEvents.inputs };
                if (pending === 0) {
                    if (callback) {
                        callback(ViewerEvents.inputs);
                    }
                    return ViewerEvents.inputs;
                }
            } else {
                ApiUtils.fetchProgramInputs(programId, (response: any) => {
                    if (response.result.status === "success") {
                        //console.log("inputs:", programId, ":" , response.inputs)
                        viewInputs[programId] = response.inputs;
                        pending--;
                        if (pending === 0) {
                            ViewerEvents.inputs = { ...viewInputs };
                            callback(viewInputs);
                        }
                    } else {
                        console.error(atob(response.message));
                    }
                });
            }
        });
    }

    static processInputs(programs: any, callback: any) {
        this.getInputs(programs, (viewInputs: any) => {
            console.log("***********inputs:", viewInputs);
            ViewerEvents.inputs = { ...viewInputs };
            ViewerEvents.newScene = true;
            checkCompositeMesh(viewInputs);
            getLayersAttributes(viewInputs, () => {
                EventsEmitter.emit("layersReady", {
                    layers: ViewerEvents.layers,
                    inputs: ViewerEvents.inputs,
                });
                if (callback) {
                    callback(viewInputs);
                }
            });
        });
    }

    static onViewLoad(viewMetaData: any, compositeMeta: any, viewer: any) {
        console.log("ViewerEvents onViewLoad...");
        ViewerEvents.viewer = viewer;
        ViewerEvents.viewMetaData = viewMetaData;
        ViewerEvents.compositeMeta = compositeMeta;
        const extendedMeta = viewer._loader?.extendedMeta;
        console.log("%cextendedMeta:", "color:green", extendedMeta);
        const inputs = viewer._inputs;
        const layers = viewer._layers;

        if (extendedMeta && extendedMeta !== null) {
            ViewerActions.extendedMeta = extendedMeta;
            if (Object.keys(layers).length > 0) {
                Object.entries(layers).forEach((entry: any) => {
                    const layerId = entry[0];
                    const entityId = getEntityId(entry[1]);
                    console.log("layerId:", layerId, "|entityId:", entityId);
                    if (entityId && extendedMeta[entityId]) {
                        LayersActions.data[layerId] = extendedMeta[entityId];
                    }
                });
            }
        }

        console.log("%cLayersActions.data:", "color:green", LayersActions.data);

        if (inputs && inputs !== null) {
            ViewerEvents.inputs = inputs;
        }
        if (layers && layers !== null) {
            ViewerEvents.layers = layers;
        }
        ViewerActions.init(ViewerEvents.viewer, viewMetaData, compositeMeta);
        //getLayersAttributes(ViewerEvents.inputs, () => {
        //   ViewerEvents.loadLayersAttributes();
        //});
        loadTempEntities();
        if (compositeMeta && !CrossSectionBounds.initializeBounds) {
            CrossSectionBounds.initCrossSectionBounds(compositeMeta, ViewerEvents.currentProject);
        }
        if (viewMetaData) {
            ViewerEvents.startView();
            ViewerEvents.processInputs(ViewerEvents.getPrograms(viewMetaData), () => {
                ViewerEvents.metaDataChanged = true;
                ViewerEvents.newScene = false;
            });
        }
        EventsEmitter.emit("onViewLoad", {
            newScene: ViewerEvents.newScene,
            layers: ViewerEvents.layers,  
            inputs: ViewerEvents.inputs, 
            view: ViewerEvents.view
        });
    }

    static onNewScene() {
        //console.log("ViewerEvents onNewScene...");
        var viewer = ViewerEvents.viewer;
        if (!ViewerEvents.newScene) {
            ViewerEvents.newScene = true;
        }
        if (ViewerEvents.metaDataChanged) {
            EventsEmitter.emit("newScene", {
                status: true,
            });
            getLayersAttributes(ViewerEvents.inputs, () => {
                ViewerEvents.loadLayersAttributes();
                checkCompositeMesh(ViewerEvents.inputs);
                EventsEmitter.emit("metadataChanged", {
                    viewMetaData: ViewerEvents.viewMetaData,
                    layers: ViewerEvents.layers,
                    inputs: ViewerEvents.inputs,
                });
                ViewerEvents.metaDataChanged = false;
            });
        }
        if (viewer && viewer.getViewer()) {
            let stats = viewer.getViewer().getStats();
            StatsActions.setStats(stats);
        }
        if (!isFirstTime) {
            console.timeEnd("*********completeLoadingTime");
            isFirstTime = true;
        }
    }

    static onBusy(isBusy: boolean) {
        EventsEmitter.emit("busy", {
            layers: ViewerEvents.layers,
            inputs: ViewerEvents.inputs,
            isBusy: isBusy,
        });
    }

    static onError(error: string) {
        EventsEmitter.emit("criticalError", {
            message: error,
            redirect: true,
        });
    }

    static onCameraRotate(event: any) {
        //To implement ?
    }

    static onCameraChange(event: any) {
        const viewer: any = ViewerEvents.viewer;
        if (!viewer || viewer === null) {
            //console.log("Discarded onCameraChange Event..");
            return;
        }

        const ortho: boolean = event.ortho;

        const scale: number = ortho ? viewer.getOrthographicScale() : 0.0;
        EventsEmitter.emit("cameraChange", scale);
        //To implement azimuth, plunge ?
    }

    static onSimDateChange(event: any) {
        const viewer: any = ViewerEvents.viewer;
        if (!viewer || viewer === null) {
            //console.log("Discarded onSimDateChange Event..");
            return;
        }

        const date: Date = event.date;
        const entity: string = event.entityId;
        const locales: Intl.LocalesArgument = viewer.getProjectLocales();
        const timeZone: string = viewer.getProjectTimeZone();

        const eventData: any = {
            date,
            entity,
            locales,
            timeZone,
        };
        EventsEmitter.emit("onSimDateChange", eventData);
    }

    static onProcessMetaData(viewMetaData: any, callback: any) {
        const programs = ViewerEvents.getPrograms(viewMetaData);
        ViewerEvents.getInputs(programs, (inputs: any) => {
            ViewerEvents.loadLayerAttributes(viewMetaData, inputs);
            callback(viewMetaData);
        });
    }

    static createLayers() {
        console.log("ViewerEvents.CreateLayers...");
        if (this.viewer && !this.viewMetaData && this.viewer._viewMetaData && this.viewer._viewMetaData !== null) {
            this.viewMetaData = this.viewer._viewMetaData;
        }
        if (this.viewMetaData && this.viewMetaData !== null) {
            for (var i in this.viewMetaData) {
                var element = ViewerEvents.findElement(i);
                if (element) {
                    this.layers[i] = element;
                }
            }
        }
    }

    static getLayers() {
        return this.layers;
    }

    static startView() {
        console.log("ViewerEvents startView...");
        if (this.view) {
            var viewType = this.view.view_type;
            if (viewType === "container") {
                this.createLayers();
                if (!this.cameraPosInitialized) {
                    this.cameraPosInitialized = true;
                    var cameraOriginX = this.view["camera_x"];
                    var cameraOriginY = this.view["camera_y"];
                    var cameraOriginZ = this.view["camera_z"];
                    var focusOriginX = this.view["focus_x"];
                    var focusOriginY = this.view["focus_y"];
                    var focusOriginZ = this.view["focus_z"];

                    if (
                        cameraOriginX &&
                        cameraOriginX !== "undefined" &&
                        cameraOriginY &&
                        cameraOriginY !== "undefined" &&
                        cameraOriginZ &&
                        cameraOriginZ !== "undefined" &&
                        focusOriginX &&
                        focusOriginX !== "undefined" &&
                        focusOriginY &&
                        focusOriginY !== "undefined" &&
                        focusOriginZ &&
                        focusOriginZ !== "undefined" &&
                        this.viewer &&
                        this.viewer !== null
                    ) {
                        this.thumbnailPosCaptured = true;
                        this.selectedViewCameraOriginCapture = {
                            x: parseFloat(cameraOriginX),
                            y: parseFloat(cameraOriginY),
                            z: parseFloat(cameraOriginZ),
                        };
                        this.selectedViewFocusOriginCapture = {
                            x: parseFloat(focusOriginX),
                            y: parseFloat(focusOriginY),
                            z: parseFloat(focusOriginZ),
                        };
                        this.viewer
                            .getViewer()
                            .setThumbnailPos(
                                parseFloat(cameraOriginX),
                                parseFloat(cameraOriginY),
                                parseFloat(cameraOriginZ),
                                parseFloat(focusOriginX),
                                parseFloat(focusOriginY),
                                parseFloat(focusOriginZ)
                            );
                    }
                }
            }
        }
    }

    static zoomToObjects() {
        var ids = getZoomToLayersIds(this.layers, this.inputs);
        getEntityBounds(ids, this.currentProject, (bounds: any) => {
            handleLayerEvent("frameWorldBounds", bounds, null);
        });
    }

    static getAttributeIndex(attributeName: string, entity: any) {
        const attributeCount: number =
            entity && typeof entity["attribute_count"] !== "undefined" ? parseInt(entity["attribute_count"]) : 0;
        for (let attributeIndex: number = 0; attributeIndex < attributeCount; ++attributeIndex) {
            const key: string = "attribute" + attributeIndex + "_name";
            if (entity[key] === attributeName) {
                return attributeIndex;
            }
        }
        return -1;
    }

    static loadLayerAttributes(layer: any, inputs: any) {
        /**
         * code to keep for compability between different version
         */
        /* start of code */
        const { currentProject, findElement } = this;
        /* end of code */

        let colorLegendSet: boolean = false;
        let collarLegendSet: boolean = false;
        let tubeLegendSet: boolean = false;
        let drillholeSettingsSet: boolean = false;
        let attributeQuerySet: boolean = false;
        let labelQuerySet: boolean = false;
        let geoChemSet: boolean = false;

        Object.keys(layer).forEach((attribute: any) => {
            switch (attribute) {
                case "layer_visible": {
                    handleLayerEvent("toggleVisibility", null, layer);
                    break;
                }
                case "layer_point_size": {
                    handleLayerEvent("setPointSize", null, layer);
                    break;
                }
                case "layer_density_threshold":
                case "layer_density_color_min_r":
                case "layer_density_color_min_g":
                case "layer_density_color_min_b":
                case "layer_density_color_max_r":
                case "layer_density_color_max_g":
                case "layer_density_color_max_b":
                case "layer_density_min_scale":
                case "layer_density_max_scale":
                case "layer_density_alpha_multiplier":
                case "layer_density_cut_depth":
                case "layer_density_ray_march_steps":
                case "layer_density_enable_ray_march":
                case "layer_density_enable_lighting:": {
                    //TODO
                    handleLayerEvent("setDensitySettings", null, layer);
                    break;
                }
                case "layer_opacity": {
                    handleLayerEvent("setTransparency", null, layer);
                    break;
                }
                case "layer_diffuse_opacity": {
                    handleLayerEvent("setDiffuseTransparency", null, layer);
                    break;
                }
                case "color_legend_mode": {
                    if (colorLegendSet) {
                        break;
                    }
                    colorLegendSet = true;

                    if (layer["color_legend_mode"] === "2") {
                        const processCommand = (legendData: any) => {
                            var data: any = LayersActions.data[layer.ID];
                            if (data) {
                                var histogram = GradientActions.getHistogram(layer.ID, legendData.colorAttributeIndex);
                                legendData.distribution = GradientActions.getDistribution(histogram, layer);
                                var attribute = layer["color_legend_attribute"];
                                if (layer["color_legend_data_driven_equal_minmax_data"] === "1") {
                                    if (typeof attribute !== "undefined") {
                                        var attributeIndex = ViewerEvents.getAttributeIndex(attribute, data);
                                        if (attributeIndex > -1) {
                                            layer["color_legend_attribute_index"] = attributeIndex + "";
                                            legendData["colorAttributeIndex"] = attributeIndex;
                                            layer["color_legend_range_min"] =
                                                typeof data["attribute" + attributeIndex + "_bound_min"] !== "undefined"
                                                    ? data["attribute" + attributeIndex + "_bound_min"]
                                                    : "0";
                                            legendData["rangeMin"] = parseFloat(layer["color_legend_range_min"]);
                                            layer["color_legend_range_max"] =
                                                typeof data["attribute" + attributeIndex + "_bound_max"] !== "undefined"
                                                    ? data["attribute" + attributeIndex + "_bound_max"]
                                                    : "1";
                                            legendData["rangeMax"] = parseFloat(layer["color_legend_range_max"]);
                                            layer["color_legend_range_step"] = "1";
                                            legendData["rangeStep"] = 1;
                                        }
                                    }
                                }
                                if (
                                    typeof layer["color_legend_attribute_index"] === "undefined" ||
                                    layer["color_legend_attribute_index"] === "-1"
                                ) {
                                    attributeIndex = ViewerEvents.getAttributeIndex(attribute, data);
                                    if (attributeIndex > -1) {
                                        layer["color_legend_attribute_index"] = attributeIndex + "";
                                        legendData["colorAttributeIndex"] = attributeIndex;
                                    }
                                }
                            }
                            console.log("processCommand:", legendData);
                            handleLayerEvent("setDataDrivenColor", legendData, layer);
                        };
                        const colorLegendDefault: any = { ...ColorLegendDefault };
                        const isAlpha =
                            typeof layer["alpha_attribute"] !== "undefined" ? layer["alpha_attribute"] === "1" : false;
                        const colorAttribute = typeof layer["color_legend_attribute"]
                            ? layer["color_legend_attribute"]
                            : colorLegendDefault.colorAttribute;
                        var legendData: any = {
                            statisticalMethodType:
                                typeof layer["color_legend_data_driven_method"] !== "undefined"
                                    ? parseInt(layer["color_legend_data_driven_method"])
                                    : colorLegendDefault.dataDrivenMethod,
                            statisticalIntervalType:
                                typeof layer["color_legend_data_driven_equal_mode"] !== "undefined"
                                    ? parseInt(layer["color_legend_data_driven_equal_mode"])
                                    : colorLegendDefault.dataDrivenEqualMode,
                            colorAttribute: colorAttribute,
                            colorAttributeIndex: getAttributeIndex(colorAttribute, layer),
                            rangeMin:
                                typeof layer["color_legend_range_min"] !== "undefined"
                                    ? parseFloat(layer["color_legend_range_min"])
                                    : colorLegendDefault.colorRangeMin,
                            rangeMax:
                                typeof layer["color_legend_range_max"] !== "undefined"
                                    ? parseFloat(layer["color_legend_range_max"])
                                    : colorLegendDefault.colorRangeMax,
                            rangeStep:
                                parseFloat(layer["color_legend_range_step"]) || colorLegendDefault.colorRangeStep,
                            rangeCount:
                                parseInt(layer["color_legend_range_count"]) || colorLegendDefault.colorRangeCount,
                            colorReverseGradient: isAlpha
                                ? layer["color_legend_reverse_alpha_gradient"] === "1" ||
                                  colorLegendDefault.reverseAlphaGradient
                                : layer["color_legend_reverse_gradient"] === "1" || colorLegendDefault.reverseGradient,
                            colorInterpolateContinuous:
                                layer["color_legend_interpolate_gradient"] === "1" ||
                                colorLegendDefault.interpolateGradient,
                            gradientId: isAlpha
                                ? typeof layer["color_legend_alpha_gradient"] !== "undefined"
                                    ? layer["color_legend_alpha_gradient"]
                                    : colorLegendDefault.alphaColorGradientId
                                : typeof layer["color_legend_gradient"] !== "undefined"
                                ? layer["color_legend_gradient"]
                                : colorLegendDefault.colorGradientId,
                            distribution: [],
                            useAttributeGradient: false,
                            attributeGradient: [],
                            doubleSided:
                                typeof layer["color_legend_double_sided"] !== "undefined"
                                    ? layer["color_legend_double_sided"]
                                    : false,
                            front: {
                                r:
                                    typeof layer["color_legend_static_r"] !== "undefined"
                                        ? layer["color_legend_static_r"]
                                        : 128,
                                g:
                                    typeof layer["color_legend_static_g"] !== "undefined"
                                        ? layer["color_legend_static_g"]
                                        : 128,
                                b:
                                    typeof layer["color_legend_static_b"] !== "undefined"
                                        ? layer["color_legend_static_b"]
                                        : 128,
                            },
                            back: {
                                r:
                                    typeof layer["color_legend_static_back_r"] !== "undefined"
                                        ? layer["color_legend_static_back_r"]
                                        : 128,
                                g:
                                    typeof layer["color_legend_static_back_g"] !== "undefined"
                                        ? layer["color_legend_static_back_g"]
                                        : 128,
                                b:
                                    typeof layer["color_legend_static_back_b"] !== "undefined"
                                        ? layer["color_legend_static_back_b"]
                                        : 128,
                            },
                        };

                        if (colorAttribute !== "") {
                            const attributes = getAttributesObjects(layer.ID);
                            const attribute = attributes.find((attribute: any) => attribute.name === colorAttribute);
                            let values = [];
                            if (attribute && attribute.values) {
                                values = attribute.values;
                            }
                            if (values.length > 0) {
                                layer["alpha_attribute"] = "1";
                                legendData["useAttributeGradient"] = true;
                                if (layer["color_legend_alpha_gradient"] === "custom") {
                                    fetchCustomColors(this.projectId, layer.ID, (data: any) => {
                                        if (data !== "" && JSON.parse(data)) {
                                            var attributeArray = JSON.parse(data);
                                            attributeArray.forEach((attribute: any) => {
                                                GradientActions.attributeGradient[layer.ID][attribute["name"]] =
                                                    attribute["values"];
                                                GradientActions.customGradient = Utils.HSLtoRGB(attribute["values"]);
                                            });
                                            var curAttr: any = attributeArray.map((attr: any) => {
                                                if (attr["name"] === colorAttribute) {
                                                    return attr["values"];
                                                }
                                                return [];
                                            });
                                            legendData["attributeGradient"] = curAttr[0];
                                            if (isAlpha) {
                                                if (colorAttribute !== "") {
                                                    GradientActions.attributeGradient[layer.ID][colorAttribute] =
                                                        curAttr[0];
                                                }
                                                GradientActions.customGradient = Utils.HSLtoRGB(curAttr[0]);
                                                //viewerTools.setState({ needUpdate: true });
                                            }
                                            processCommand(legendData);
                                        } else {
                                            if (
                                                GradientActions.attributeGradient[layer.ID] &&
                                                GradientActions.attributeGradient[layer.ID][colorAttribute]
                                            ) {
                                                legendData["attributeGradient"] =
                                                    GradientActions.attributeGradient[layer.ID][colorAttribute];
                                            } else {
                                                if (isAlpha) {
                                                    layer["color_legend_alpha_gradient"] = "color";
                                                    GradientActions.attributeGradient[layer.ID][colorAttribute] =
                                                        GradientActions.getAttributeGradient(layer);

                                                    legendData["attributeGradient"] =
                                                        GradientActions.attributeGradient[layer.ID][colorAttribute];
                                                } else {
                                                    legendData["attributeGradient"] = [];
                                                }
                                            }
                                            processCommand(legendData);
                                        }
                                    });
                                } else {
                                    legendData["attributeGradient"] =
                                        typeof layer["color_legend_alpha_gradient"] !== "undefined"
                                            ? Utils.rgbToHSL(GradientActions.getAlphaGradient(layer, values))
                                            : [];
                                }
                            }
                        }
                        console.log("colorLegend.legendData:", legendData);
                        processCommand(legendData);
                    } else if (layer["color_legend_mode"] === "1") {
                        var colorLegendData = {
                            doubleSided:
                                typeof layer["color_legend_double_sided"] !== "undefined"
                                    ? layer["color_legend_double_sided"]
                                    : false,
                            front: {
                                r:
                                    typeof layer["color_legend_static_r"] !== "undefined"
                                        ? layer["color_legend_static_r"]
                                        : 128,
                                g:
                                    typeof layer["color_legend_static_g"] !== "undefined"
                                        ? layer["color_legend_static_g"]
                                        : 128,
                                b:
                                    typeof layer["color_legend_static_b"] !== "undefined"
                                        ? layer["color_legend_static_b"]
                                        : 128,
                            },
                            back: {
                                r:
                                    typeof layer["color_legend_static_back_r"] !== "undefined"
                                        ? layer["color_legend_static_back_r"]
                                        : 128,
                                g:
                                    typeof layer["color_legend_static_back_g"] !== "undefined"
                                        ? layer["color_legend_static_back_g"]
                                        : 128,
                                b:
                                    typeof layer["color_legend_static_back_b"] !== "undefined"
                                        ? layer["color_legend_static_back_b"]
                                        : 128,
                            },
                        };
                        handleLayerEvent("setStaticColor", colorLegendData, layer);
                    }
                    break;
                }
                case "collar_legend_mode":
                case "collar_legend_color":
                case "collar_legend_radius": {
                    if (collarLegendSet) {
                        break;
                    }
                    collarLegendSet = true;

                    const collarMode = layer["collar_legend_mode"] || "1";
                    const collarColour = layer["collar_legend_color"] || "#ffffff";
                    const collarSize = layer["collar_legend_radius"] || 10.0;
                    const collarTextSize = layer["collar_legend_text_size"] || 1.0;
                    const collarData = {
                        collarMode: collarMode,
                        collarColour: collarColour,
                        collarSize: collarSize,
                        collarTextSize: collarTextSize,
                    };
                    handleLayerEvent("setCollarLegend", collarData, layer);
                    break;
                }
                case "tube_legend_scale_mode":
                case "tube_legend_radius":
                case "tube_legend_attribute":
                case "tube_legend_range_min":
                case "tube_legend_range_max":
                case "tube_legend_range_step":
                case "tube_legend_radius_min":
                case "tube_legend_radius_max": {
                    if (tubeLegendSet) {
                        break;
                    }
                    tubeLegendSet = true;
                    const layerData = LayersActions.data[layer.ID];

                    const scaleMode: string = layer["tube_legend_scale_mode"] || "1";
                    const cylinderRadius: number = layer["tube_legend_radius"] || 10.0;
                    const attribute: string = layer["tube_legend_attribute"] || "";
                    const attributeIndex: number = ViewerEvents.getAttributeIndex(attribute, layerData);
                    const rangeMin: number = layer["tube_legend_range_min"] || 0.0;
                    const rangeMax: number = layer["tube_legend_range_max"] || 100.0;
                    const rangeStep: number = layer["tube_legend_range_step"] || 1.0;
                    const radiusMin: number = layer["tube_legend_radius_min"] || 1.0;
                    const radiusMax: number = layer["tube_legend_radius_max"] || 10.0;

                    const data = {
                        scaleMode: scaleMode,
                        cylinderRadius: cylinderRadius,
                        attribute: attribute,
                        attributeIndex: attributeIndex,
                        rangeMin: rangeMin,
                        rangeMax: rangeMax,
                        rangeStep: rangeStep,
                        radiusMin: radiusMin,
                        radiusMax: radiusMax,
                    };

                    handleLayerEvent("setTubeDiameterLegend", data, layer);
                    break;
                }
                case "geo_chem_show_z":
                case "geo_chem_show_labels":
                case "geo_chem_show_elevation":
                case "geo_chem_plane_value":
                case "geo_chem_top_attribute":
                case "geo_chem_top_color":
                case "geo_chem_top_index":
                case "geo_chem_bottom_attribute":
                case "geo_chem_bottom_color":
                case "geo_chem_bottom_index":
                case "geo_chem_left_attribute":
                case "geo_chem_left_color":
                case "geo_chem_left_index":
                case "geo_chem_right_attribute":
                case "geo_chem_right_color":
                case "geo_chem_right_index":
                case "geo_chem_label_size":
                case "geo_chem_size":
                case "geo_chem_shape": {
                    if (geoChemSet) {
                        break;
                    }
                    geoChemSet = true;
                    const geoChemSetting = getDrillHoleSettings(layer);
                    const {
                        geoShowZ,
                        geoShowLabels,
                        geoLabelSize,
                        geoShowElevation,
                        geoPlaneValue,
                        geoAttributes,
                        geoSize,
                        geoShape,
                    } = geoChemSetting;
                    const geoChemSettingData = {
                        showPolygon: false,
                        showInside: true,
                        polygonPoints: { vfPoints: [], wPoints: [] },
                        showZ: geoShowZ,
                        geoShowLabels: geoShowLabels,
                        geoLabelSize: geoLabelSize,
                        geoShowElevation: geoShowElevation,
                        planeValue: geoPlaneValue,
                        attributes: geoAttributes,
                        geoSize: geoSize,
                        geoShape: geoShape,
                    };
                    handleLayerEvent("setGeoChemSettings", geoChemSettingData, layer);
                    break;
                }
                case "tube_legend_trace_thickness":
                    const traceThickness: number = layer["tube_legend_trace_thickness"] || 10.0;
                    const traceData = {
                        traceThickness: traceThickness,
                    };
                    handleLayerEvent("setTraceSettings", traceData, layer);
                    break;
                case "drillholes_show_collars":
                case "drillholes_show_collar_labels":
                case "drillholes_show_cylinders":
                case "drillholes_show_attributes":
                case "drillholes_attributes_text_size":
                case "drillholes_attributes": {
                    if (drillholeSettingsSet) {
                        break;
                    }
                    drillholeSettingsSet = true;
                    const layerData = LayersActions.data[layer.ID];

                    //Lambda
                    const getAttributeIndices = function (colorAttributeListIndex: number, colorAttributes: string) {
                        let attributeIndices = [];

                        if (typeof colorAttributes !== "undefined") {
                            const elements: string[] = colorAttributes.split(" ");
                            const elementCount: number = elements.length;

                            for (let elementIndex: number = 0; elementIndex < elementCount; ++elementIndex) {
                                const element: string = elements[elementIndex];
                                if (element !== "") {
                                    const subElements: string[] = element.split(",");
                                    const attributeName: string = subElements[0];
                                    const attributeIndex: number = ViewerEvents.getAttributeIndex(
                                        attributeName,
                                        layerData
                                    );
                                    const attributeListIndex: number = parseInt(subElements[1]);
                                    if (attributeListIndex === colorAttributeListIndex && attributeIndex > -1) {
                                        attributeIndices.push(attributeIndex);
                                    }
                                }
                            }
                        }

                        return attributeIndices;
                    };

                    const showCollars: boolean = fromRESTBool(layer["drillholes_show_collars"]);
                    const showCollarLabels: boolean = fromRESTBool(layer["drillholes_show_collar_labels"]);
                    const showCylinders: boolean = fromRESTBool(layer["drillholes_show_cylinders"]);
                    const showAttributes: boolean = fromRESTBool(layer["drillholes_show_attributes"]);
                    const attributes: string = layer["drillholes_attributes"] || "";
                    const leftAttributeIndices: number[] = getAttributeIndices(0, attributes);
                    const rightAttributeIndices: number[] = getAttributeIndices(1, attributes);
                    const attributesTextSize: number = parseInt(layer["drillholes_attributes_text_size"]) || 1.0;
                    const drillholeSettingData: any = {
                        showCollars: showCollars,
                        showCollarLabels: showCollarLabels,
                        showCylinders: showCylinders,
                        showAttributes: showAttributes,
                        attributes: attributes,
                        leftAttributeIndices: leftAttributeIndices,
                        rightAttributeIndices: rightAttributeIndices,
                        attributesTextSize: attributesTextSize,
                        attributeRawData: attributes,
                    };

                    handleLayerEvent("setDrillholeSettings", drillholeSettingData, layer);
                    break;
                }
                case "attribute_query_mode": {
                    if (attributeQuerySet) {
                        break;
                    }
                    attributeQuerySet = true;

                    const attributeQueryMode = layer["attribute_query_mode"];
                    if (attributeQueryMode === "1") {
                        let inputId: string = "";
                        if (inputs && inputs[layer.view_type]) {
                            let obj: any = Object.values(inputs[layer.view_type]).find(
                                (elem: any) => elem.type === "5"
                            );
                            if (obj) {
                                inputId = inputs[layer.view_type][obj.entity].id;
                            }
                        }
                        const entityId = layer["input_value_" + inputId];
                        const entity: any = currentProject[entityId];
                        const attributeCount: number = typeof entity !== "undefined" ? entity["attribute_count"] : 0;
                        const layerData = LayersActions.data[layer.ID];

                        //Lambda
                        const getValueCount = (attributeName: string) => {
                            for (let attributeIndex: number = 0; attributeIndex < attributeCount; ++attributeIndex) {
                                if (entity["attribute" + attributeIndex + "_name"] === attributeName) {
                                    const attributeType = parseInt(entity["attribute" + attributeIndex + "_type"]);
                                    if (attributeType === 1) {
                                        const attributeSetValueCount = parseInt(
                                            entity["attribute" + attributeIndex + "_set_count"]
                                        );
                                        return attributeSetValueCount;
                                    }
                                }
                            }
                            return 0;
                        };

                        //Lambda
                        const getValueIndex = (attributeName: string, attributeSetValueName: string) => {
                            for (let attributeIndex: number = 0; attributeIndex < attributeCount; ++attributeIndex) {
                                if (entity["attribute" + attributeIndex + "_name"] === attributeName) {
                                    const attributeType = parseInt(entity["attribute" + attributeIndex + "_type"]);
                                    if (attributeType === 1) {
                                        const attributeSetValueCount = parseInt(
                                            entity["attribute" + attributeIndex + "_set_count"]
                                        );
                                        for (
                                            let attributeSetValueIndex = 0;
                                            attributeSetValueIndex < attributeSetValueCount;
                                            ++attributeSetValueIndex
                                        ) {
                                            if (
                                                entity[
                                                    "attribute" + attributeIndex + "_set_value" + attributeSetValueIndex
                                                ] === attributeSetValueName
                                            ) {
                                                return attributeSetValueIndex;
                                            }
                                        }
                                    }
                                }
                            }
                            return -1;
                        };

                        //Lambda
                        const getBuilderAttributeFilters = function (builderColorAttributes: string) {
                            let attributeFilters: any[] = [];

                            if (typeof builderColorAttributes !== "undefined") {
                                const elements: string[] = builderColorAttributes.split(" ");
                                const elementCount: number = elements.length;
                                const queryType: any = {
                                    Number: 0,
                                    String: 1,
                                };
                                for (let elementIndex: number = 0; elementIndex < elementCount; ++elementIndex) {
                                    const element: string = elements[elementIndex];
                                    if (element !== "") {
                                        const subElements: string[] = element.split(",");
                                        const attributeName: string = subElements[0];
                                        const attributeIndex: number = ViewerEvents.getAttributeIndex(
                                            attributeName,
                                            layerData
                                        );
                                        const attributeOperator: string = subElements[3];
                                        if (subElements[1] === "") {
                                            const value = subElements[2];
                                            if (value !== "") {
                                                const valueCount: number = getValueCount(attributeName);
                                                if (valueCount > 0) {
                                                    const valueIndex: number = getValueIndex(attributeName, value);
                                                    if (valueIndex !== -1) {
                                                        attributeFilters.push({
                                                            attributeIndex: attributeIndex,
                                                            attributeName: attributeName,
                                                            attributeSetValueName: value,
                                                            attributeSetValueIndex: valueIndex,
                                                            attributeOperator: attributeOperator,
                                                            attributeType: queryType.String,
                                                        });
                                                    }
                                                } else {
                                                    attributeFilters.push({
                                                        attributeIndex: attributeIndex,
                                                        attributeName: attributeName,
                                                        attributeStringValue: value,
                                                        attributeOperator: attributeOperator,
                                                        attributeType: queryType.String,
                                                    });
                                                }
                                            } else {
                                                //INVALID ATTRIBUTESETVALUENAME
                                                console.log("VE. Filter Ignored");
                                            }
                                        } else {
                                            const attributeMin: number = parseFloat(subElements[1]);
                                            const attributeMax: any = parseFloat(subElements[2]);

                                            attributeFilters.push({
                                                attributeIndex: attributeIndex,
                                                attributeName: attributeName,
                                                attributeMin: attributeMin,
                                                attributeMax: attributeMax,
                                                attributeOperator: attributeOperator,
                                                attributeType: queryType.Number,
                                            });
                                        }
                                    }
                                }
                            }

                            return attributeFilters;
                        };

                        const builderAttributes: string = layer["attribute_query_builder"] || "";
                        const builderAttributeFilters: any[] = getBuilderAttributeFilters(builderAttributes);

                        const attributeQueryData: any = {
                            attributeQueryBuilderValue: builderAttributes,
                            builderAttributeFilters: builderAttributeFilters,
                            builderAttributeRawData: builderAttributes,
                        };
                        handleLayerEvent("setAttributeQueryBuilder", attributeQueryData, layer);
                    } else if (attributeQueryMode === "2") {
                        const textAttributes: string = layer["attribute_query_string"] || "";

                        const attributeQueryData: any = {
                            attributeQueryString: textAttributes,
                            textAttributeRawData: textAttributes,
                        };

                        handleLayerEvent("setAttributeQueryString", attributeQueryData, layer);
                    }
                    break;
                }
                case "label_query_mode": {
                    if (labelQuerySet) {
                        break;
                    }
                    labelQuerySet = true;

                    const labelQueryMode = layer["label_query_mode"];
                    if (labelQueryMode === "1") {
                        //Lambda
                        const getBuilderLabelFilters = function (builderLabels: string) {
                            let labelFilters: /*any*/ string[] = [];

                            if (typeof builderLabels !== "undefined") {
                                const elements: string[] = builderLabels.split(" ");
                                const elementCount: number = elements.length;

                                for (let elementIndex: number = 0; elementIndex < elementCount; ++elementIndex) {
                                    const element: string = elements[elementIndex];
                                    if (element !== "") {
                                        labelFilters.push(
                                            /*{*/
                                            element
                                            /*}*/
                                        );
                                    }
                                }
                            }

                            return labelFilters;
                        };

                        const builderLabels: string = layer["label_query_builder"] || "";
                        var polygonPoints = ViewerActions.getPolygonPoints(layer);
                        const showPolygon: boolean = layer["show_polygon_query_builder"] === "1" || false;
                        if (!showPolygon) {
                            polygonPoints = {
                                vfPoints: [],
                                wPoints: [],
                            };
                        }
                        const showOutside: boolean = layer["show_polygon_outside"] === "1" || false;
                        const builderLabelFilters: /*any*/ string[] = getBuilderLabelFilters(builderLabels);

                        const labelQueryData: any = {
                            type: 1,
                            labelQueryBuilderValue: builderLabels,
                            builderLabelFilters: builderLabelFilters,
                            builderLabelRawData: builderLabels,
                            showPolygon: showPolygon,
                            showInside: !showOutside,
                            polygonPoints: polygonPoints,
                        };

                        handleLayerEvent("setDrillHolesLabelQuery", labelQueryData, layer);
                    } else if (labelQueryMode === "2") {
                        const textLabels: string = layer["label_query_string"] || "";

                        const labelQueryData: any = {
                            labelQueryString: textLabels,
                            textLabelRawData: textLabels,
                        };

                        handleLayerEvent("setLabelQueryString", labelQueryData, layer);
                    }
                    break;
                }
                case "layer_mesh_properties": {
                    console.log("To complete mesh properties handling");
                    break;
                }
                case "raster_list": {
                    const compositeRasters: any = [];
                    const rasterList = layer["raster_list"].trim().split(" ");
                    rasterList.forEach((rasterId: string) => {
                        var raster = findElement(rasterId);
                        if (raster) {
                            var imageProperties = RasterActions.getImageProperties(raster);
                            compositeRasters.push(raster);
                            handleLayerEvent(
                                "setImagePropertiesAt",
                                {
                                    ...imageProperties,
                                    image: raster,
                                },
                                layer
                            );
                        }
                    });
                    ViewerActions.compositeRasters[layer.ID] = compositeRasters;
                    break;
                }
                case "index_filter": {
                    const index_filter = parseInt(layer["index_filter"]);
                    if (index_filter > 0) {
                        var newBounds = ViewerActions.getBlockModelBounds(layer, inputs);
                        const checkBoxState = getLayerSettings(
                            layer,
                            ["aabb_step_enable_x", "aabb_step_enable_y", "aabb_step_enable_z"],
                            [true, true, true]
                        );
                        var { aabbStepEnableX, aabbStepEnableY, aabbStepEnableZ } = checkBoxState;
                        const blockModelLimits: any = {
                            indexFilter: layer["index_filter"] ? parseInt(layer["index_filter"]) : 0, //0: none; 1:subset; 2:sliced
                            regionType: 0, //0:box
                            bounds: newBounds,
                            enable: {
                                x: aabbStepEnableX,
                                y: aabbStepEnableY,
                                z: aabbStepEnableZ,
                            },
                        };
                        //console.log('bounds:', bounds);
                        //console.log('newBounds:', newBounds);
                        handleLayerEvent("setBlockModelLimits", blockModelLimits, layer);
                    }
                    break;
                }
            }
        });
    }

    static loadContainerAttributes(selectedViewContainerProperties: any, inputs: any) {
        Object.values(selectedViewContainerProperties).forEach((layer: any) => {
            ViewerEvents.loadLayerAttributes(layer, inputs);
        });
    }

    static loadLayersAttributes() {
        if (ViewerEvents.layers) {
            var { inputs } = ViewerEvents;
            Object.values(ViewerEvents.layers).forEach((layer: any) => {
                ViewerEvents.loadLayerAttributes(layer, inputs);
            });
        }
    }
    static AddEntityToCurrentProject(entity: any) {
        ViewerEvents.currentProject[entity.ID] = entity;
    }
}
